import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  FALLBACK_STRING_DASH,
  FALLBACK_STRING_NA,
} from 'src/constants/global-strings'
import { useGetVesselArchivedDocuments } from 'src/hooks/queries/vessels/documents/use-get-vessel-archived-documents'
import {
  AddIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronUpIcon,
  CloseIcon,
  ExportIcon,
  FileIcon,
  ImportIcon,
  LinkIcon,
} from '~components/icons'
import {
  TSetStatus,
  useAnimatedAlert,
} from '~components/molecules/animated-alert/animated-alert-provider'
import { TabTooltipDynamicContent } from '~components/organisms/tooltips/tab-tooltip-dynamic-content'
import { ScrollHintTableContainer } from '~components/scroll-hint-table-container'
import { Tooltip } from '~components/tooltip'
import { useHasScope } from '~hooks/permissions/use-has-scope'
import {
  IArchivedDocument,
  IDocumentsRowData,
} from '~hooks/queries/vessels/documents/types'
import { useDateFormat } from '~hooks/use-date-format'
import { useDocumentDownloads } from '~hooks/use-document-downloads'
import { DialogBox } from '~pages/pages-behind-login/chartering/pre-fixture-page/components/dialog-box'
import { useVesselPageState } from '~pages/pages-behind-login/vessel-page/vessel-page-provider'
import { COLORS } from '~theme/colors'
import { Z_INDEX } from '~types/z-index'
import { capitalizeFirstLetter } from '~utils/capitalize-first-letter'
import { isDateExpired, parseDateOnly, toDateFormat } from '~utils/dates'
import DocumentsPreviewDownloadControls from '../components/documents-preview-download-controls'
import { isPrimaryCertificateType } from '../document-types'
import { ElectronicDocumentLabel } from '../components/electronic-document-label'
import {
  AddDocumentModal,
  certificateIssuerOptions,
} from '../modals/add-document-modal'
import { DocumentsEditModalWithTrigger } from '../modals/documents-edit-modal'
import { TMDocumentModal } from '../modals/tm-document-modal'

interface DocumentsTableMuiProps {
  certificateStatus?: ReactNode
  documents: IDocumentsRowData[]
  isCommercialView: boolean
  onCommercialViewChange: (checked: boolean) => void
  refetchDocuments?: () => void
  showTechManagerDocuments: boolean
}

interface DocumentTypeGroup {
  type: string
  documents: IDocumentsRowData[]
}

type SortField = 'documentName' | 'expires'
type SortDirection = 'asc' | 'desc'

const STATUS_COLUMN_WIDTH = 20
const REFERENCE_NUMBER_COLUMN_WIDTH = 65
const DOCUMENT_NAME_COLUMN_WIDTH = 300
const ACTIONS_COLUMN_WIDTH = 65
const ARCHIVED_EXPAND_COLUMN_WIDTH = 20
const ARCHIVED_DOCUMENTS_BORDER = `2px solid ${COLORS.greys.light}`

export function useCanEditAndAddDocument() {
  const { vesselId } = useParams()
  return useHasScope(
    `vessel:${vesselId}:poolpartner`,
    `vessel:${vesselId}:crew`,
    `vessel:${vesselId}:mtops`,
    'vessel:*:mtops',
    '*:*:mtuser',
    '*:*:accountmanager',
  )
}

function getDocumentIcon(tableRowItem: IDocumentsRowData) {
  const isExpired = isDateExpired(tableRowItem.expires)

  if (tableRowItem.source === 'N/A') {
    return <CheckIcon size={18} color={COLORS.green.mid} />
  }

  if (isExpired) {
    return (
      <Tooltip
        size={18}
        icon="warning"
        color={COLORS.red.mid}
        title={<TabTooltipDynamicContent text="Document expired." />}
      />
    )
  }

  if (!tableRowItem.files) {
    return (
      <Tooltip
        size={18}
        icon="info"
        color={COLORS.secondary.darkBlue}
        title={
          <TabTooltipDynamicContent
            text={`Document not available in source: ${tableRowItem.source ?? 'source'}. Edit document to upload your own version.`}
          />
        }
      />
    )
  }

  return <FileIcon size={18} color={COLORS.secondary.blue} />
}

function sortDocuments(
  documents: IDocumentsRowData[],
  sortField: SortField,
  sortDirection: SortDirection,
): IDocumentsRowData[] {
  return [...documents].sort((a, b) => {
    if (sortField === 'documentName') {
      const comparison = a.documentName.localeCompare(b.documentName)
      return sortDirection === 'asc' ? comparison : -comparison
    }
    // Sort by expires
    const aDate = a.expires ? new Date(a.expires).getTime() : null
    const bDate = b.expires ? new Date(b.expires).getTime() : null
    // Null values go to the end
    if (aDate === null && bDate === null) return 0
    if (aDate === null) return 1
    if (bDate === null) return -1
    return sortDirection === 'asc' ? aDate - bDate : bDate - aDate
  })
}

function DocumentTypeRow({
  group,
  vesselId,
  sortField,
  sortDirection,
  refetchDocuments,
  archivedDocuments,
  showingArchived,
  existingDocumentTags,
  showTechManagerDocuments,
}: {
  group: DocumentTypeGroup
  vesselId: string
  sortField: SortField
  sortDirection: SortDirection
  refetchDocuments?: () => void
  archivedDocuments?: IArchivedDocument[]
  showingArchived?: boolean
  existingDocumentTags: string[]
  showTechManagerDocuments: boolean
}) {
  const [open, setOpen] = useState(true)
  const state = useVesselPageState()
  const { setStatus } = useAnimatedAlert()
  const dateFormat = useDateFormat()
  const selectables = group.documents.filter((doc) => doc.files)
  const showCheckbox = state.isBulkSelectActive && selectables.length > 0
  const isChecked =
    state.selectedDocuments.length > 0 &&
    selectables.every((doc) =>
      state.selectedDocuments.some((d) => d.documentName === doc.documentName),
    )

  const sortedDocuments = sortDocuments(
    group.documents,
    sortField,
    sortDirection,
  )

  const groupColSpan =
    (showTechManagerDocuments ? 13 : 7) + (showingArchived ? 1 : 0)

  return (
    <>
      <TableRow>
        {showCheckbox && (
          <TableCell sx={{ textAlign: 'center' }}>
            <Checkbox
              sx={{ p: 0 }}
              checked={isChecked}
              onChange={() => {
                if (isChecked) {
                  state.setSelectedDocuments(
                    state.selectedDocuments.filter(
                      (d) =>
                        !selectables.some(
                          (s) => s.documentName === d.documentName,
                        ),
                    ),
                  )
                } else {
                  const newDocs = selectables.filter(
                    (s) =>
                      !state.selectedDocuments.some(
                        (d) => d.documentName === s.documentName,
                      ),
                  )
                  state.setSelectedDocuments([
                    ...state.selectedDocuments,
                    ...newDocs,
                  ])
                }
              }}
            />
          </TableCell>
        )}
        <TableCell colSpan={groupColSpan}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="paragraph2">{group.type}</Typography>
            <IconButton
              className="tour-step-documents-section-header"
              onClick={() => setOpen(!open)}
            >
              {open ? (
                <ChevronDownIcon width="20px" height="20px" />
              ) : (
                <ChevronLeftIcon width="20px" height="20px" />
              )}
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
      {open && (
        <>
          {sortedDocuments.map((doc) => (
            <DocumentRow
              key={`${doc.documentName}-${doc.source}`}
              document={doc}
              vesselId={vesselId}
              setStatus={setStatus}
              dateFormat={dateFormat}
              refetchDocuments={refetchDocuments}
              archivedDocuments={archivedDocuments?.filter(
                (archived) =>
                  archived.metadata.documentName === doc.documentName,
              )}
              showingArchived={showingArchived}
              existingDocumentTags={existingDocumentTags}
              showTechManagerDocuments={showTechManagerDocuments}
            />
          ))}
        </>
      )}
    </>
  )
}

function DocumentRow({
  document,
  vesselId,
  setStatus,
  dateFormat,
  refetchDocuments,
  archivedDocuments,
  showingArchived,
  existingDocumentTags,
  showTechManagerDocuments,
}: {
  document: IDocumentsRowData
  vesselId: string
  setStatus: TSetStatus
  dateFormat: string
  refetchDocuments?: () => void
  archivedDocuments?: IArchivedDocument[]
  showingArchived?: boolean
  existingDocumentTags: string[]
  showTechManagerDocuments: boolean
}) {
  const dateFormatWithHoursAndMinutes = useDateFormat('with hours and minutes')
  const state = useVesselPageState()
  const { downloadDocuments } = useDocumentDownloads(vesselId)
  const [archivedOpen, setArchivedOpen] = useState(false)

  const hasArchivedDocuments = archivedDocuments && archivedDocuments.length > 0

  const isDocumentExpired = document.expires && isDateExpired(document.expires)
  const isQ88Document = document.source === 'Q88'
  const canAddDocument =
    (isDocumentExpired && isQ88Document) || (document.files && isQ88Document)

  const isChecked = state.selectedDocuments.some(
    (d) => d.documentName === document.documentName,
  )

  const handleCheckboxChange = () => {
    if (isChecked) {
      state.setSelectedDocuments(
        state.selectedDocuments.filter(
          (d) => d.documentName !== document.documentName,
        ),
      )
    } else {
      state.setSelectedDocuments([...state.selectedDocuments, document])
    }
  }

  const isDocumentWithAuditWindow =
    [
      'Safety Management Certificates',
      'International Ship Security Certificate (ISSC)',
    ].includes(document.documentName) && showTechManagerDocuments

  const backgroundColor = isDocumentWithAuditWindow
    ? COLORS.greys.light
    : COLORS.greys.greyTint

  const totalColumns =
    (showTechManagerDocuments ? 13 : 7) + (showingArchived ? 1 : 0)
  const isArchivedSectionOpen = Boolean(
    showingArchived && hasArchivedDocuments && archivedOpen,
  )

  return (
    <>
      <TableRow
        sx={{
          backgroundColor,
          ...(isArchivedSectionOpen && {
            position: 'relative',
            zIndex: 1,
            boxShadow: 'none',
            '& > .MuiTableCell-root': {
              borderTop: ARCHIVED_DOCUMENTS_BORDER,
            },
            '& > .MuiTableCell-root:first-of-type': {
              borderLeft: ARCHIVED_DOCUMENTS_BORDER,
            },
            '& > .MuiTableCell-root:last-of-type': {
              borderRight: ARCHIVED_DOCUMENTS_BORDER,
            },
          }),
        }}
      >
        <TableCell
          className="tour-step-documents-status-icon"
          sx={{ width: STATUS_COLUMN_WIDTH, textAlign: 'center' }}
        >
          {state.isBulkSelectActive && document.files ? (
            <Checkbox
              sx={{ p: 0 }}
              checked={isChecked}
              onChange={handleCheckboxChange}
            />
          ) : (
            getDocumentIcon(document)
          )}
        </TableCell>
        {showTechManagerDocuments && (
          <TableCell
            className="tour-step-documents-reference-number"
            sx={{
              width: REFERENCE_NUMBER_COLUMN_WIDTH,
              maxWidth: REFERENCE_NUMBER_COLUMN_WIDTH,
              overflowWrap: 'anywhere',
              textAlign: 'center',
            }}
          >
            {document.referenceNumber && (
              <Typography variant="paragraph2">
                {document.referenceNumber}
              </Typography>
            )}
          </TableCell>
        )}
        <TableCell
          className="tour-step-documents-name-cell"
          sx={{
            display: 'flex',
            height: '30px',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: DOCUMENT_NAME_COLUMN_WIDTH,
          }}
        >
          {isDocumentWithAuditWindow ? (
            <>
              <Tooltip
                title={
                  document.source === 'missing'
                    ? 'No audit window calculated for this document as it is missing.'
                    : document.hasAuditWindow &&
                        document.auditWindowStart &&
                        document.auditWindowEnd
                      ? `Audit Window: ${toDateFormat(document.auditWindowStart, dateFormat)} - ${toDateFormat(document.auditWindowEnd, dateFormat)}`
                      : 'No audit window calculated for this document as it expires in less than 3 years.'
                }
                size={18}
                icon="info"
                color={COLORS.secondary.darkBlue}
              >
                {document.files ? (
                  <DocumentsPreviewDownloadControls
                    vesselId={vesselId}
                    documentName={document.documentName}
                    tableRowItem={document}
                    setStatusAnimatedAlert={setStatus}
                  />
                ) : (
                  <Typography variant="paragraph2" sx={{ display: 'block' }}>
                    {document.documentName}
                  </Typography>
                )}
              </Tooltip>
            </>
          ) : document.files ? (
            <DocumentsPreviewDownloadControls
              vesselId={vesselId}
              documentName={document.documentName}
              tableRowItem={document}
              setStatusAnimatedAlert={setStatus}
            />
          ) : (
            <Typography variant="paragraph2" sx={{ display: 'block' }}>
              {document.documentName}
            </Typography>
          )}
          {document.digitalCertificate && (
            <ElectronicDocumentLabel sx={{ ml: 2 }} />
          )}
          {document?.tags && document?.tags?.length > 0 && (
            <Tooltip
              sx={{ popper: { maxWidth: 'none' } }}
              title={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '300px',
                    p: 2,
                  }}
                >
                  <Typography variant="label" sx={{ mb: 3 }}>
                    Document Tags
                  </Typography>
                  {document.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      sx={{ mb: 2, mr: 2, width: 'fit-content' }}
                    />
                  ))}
                </Box>
              }
            >
              <Box>
                <LinkIcon size={18} color={COLORS.secondary.blue} />
              </Box>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ width: ACTIONS_COLUMN_WIDTH }}>
          <Box
            sx={{ display: 'flex', gap: 1, color: COLORS.secondary.darkBlue }}
          >
            {document.files && (
              <IconButton
                size="small"
                onClick={async () => {
                  await downloadDocuments([document], setStatus)
                }}
                aria-label="download-document"
                sx={{ color: 'inherit' }}
              >
                <ImportIcon style={{ width: 20, height: 20 }} />
              </IconButton>
            )}
            <DocumentsEditModalWithTrigger
              refetch={refetchDocuments}
              document={document}
              canAddDocument={canAddDocument}
              existingDocumentTags={existingDocumentTags}
              showTechManagerDocuments={showTechManagerDocuments}
            />
          </Box>
        </TableCell>
        {showTechManagerDocuments && (
          <TableCell>
            <Typography variant="paragraph2">
              {document.certificateIssuer
                ? certificateIssuerOptions.find(
                    (option) => option.value === document.certificateIssuer,
                  )?.label
                : FALLBACK_STRING_DASH}
            </Typography>
          </TableCell>
        )}
        {showTechManagerDocuments && (
          <TableCell>
            <Typography variant="paragraph2">
              {document.certificateNumber
                ? document.certificateNumber
                : FALLBACK_STRING_DASH}
            </Typography>
          </TableCell>
        )}
        <TableCell>
          <Typography variant="paragraph2">
            {document.issued
              ? toDateFormat(parseDateOnly(document.issued), dateFormat)
              : FALLBACK_STRING_DASH}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="paragraph2">
            {document.expires
              ? toDateFormat(parseDateOnly(document.expires), dateFormat)
              : document.files
                ? 'No Expiry Date'
                : FALLBACK_STRING_DASH}
          </Typography>
        </TableCell>
        {showTechManagerDocuments && (
          <TableCell>
            <Typography variant="paragraph2">
              {document.lastSurvey
                ? toDateFormat(parseDateOnly(document.lastSurvey), dateFormat)
                : FALLBACK_STRING_DASH}
            </Typography>
          </TableCell>
        )}
        {showTechManagerDocuments && (
          <TableCell>
            <Typography variant="paragraph2">
              {document.nextSurvey
                ? toDateFormat(parseDateOnly(document.nextSurvey), dateFormat)
                : document.files
                  ? 'No Next Survey'
                  : FALLBACK_STRING_DASH}
            </Typography>
          </TableCell>
        )}
        {showTechManagerDocuments && (
          <TableCell>
            <Typography variant="paragraph2">
              {document.remarks ? document.remarks : FALLBACK_STRING_DASH}
            </Typography>
          </TableCell>
        )}
        <TableCell>
          {document.notApplicable ? (
            <Tooltip
              style={{ color: COLORS.secondary.darkBlue }}
              size={18}
              labelText={
                <Typography variant="paragraph2">
                  {FALLBACK_STRING_NA}
                </Typography>
              }
              title={
                <TabTooltipDynamicContent text="Document is not applicable/available for this vessel." />
              }
            />
          ) : document.source ? (
            <Typography
              variant="paragraph2"
              sx={
                document.source === 'Q88'
                  ? { color: COLORS.secondary.midBlue }
                  : {}
              }
            >
              {capitalizeFirstLetter(document.source)}
            </Typography>
          ) : (
            FALLBACK_STRING_DASH
          )}
        </TableCell>
        <TableCell>
          <Typography variant="paragraph2">
            {document.updatedAt
              ? toDateFormat(document.updatedAt, dateFormat)
              : FALLBACK_STRING_DASH}
          </Typography>
        </TableCell>
        {showingArchived && archivedDocuments && (
          <TableCell
            sx={{
              width: ARCHIVED_EXPAND_COLUMN_WIDTH,
              textAlign: 'center',
            }}
          >
            {hasArchivedDocuments && (
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setArchivedOpen(!archivedOpen)}
              >
                {isArchivedSectionOpen ? (
                  <ChevronUpIcon width="16px" height="16px" />
                ) : (
                  <ChevronDownIcon width="16px" height="16px" />
                )}
              </IconButton>
            )}
          </TableCell>
        )}
      </TableRow>
      {hasArchivedDocuments && (
        <TableRow
          sx={{
            display: isArchivedSectionOpen ? 'table-row' : 'none',
            backgroundColor: COLORS.primary.white,
            position: 'relative',
            zIndex: 1,
            boxShadow: 'none',
          }}
        >
          <TableCell
            sx={{
              position: 'relative',
              p: 0,
              borderLeft: ARCHIVED_DOCUMENTS_BORDER,
              borderRight: ARCHIVED_DOCUMENTS_BORDER,
              borderBottom: ARCHIVED_DOCUMENTS_BORDER,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-8px',
                left: '-3px',
                right: '-3px',
                height: '8px',
                borderLeft: ARCHIVED_DOCUMENTS_BORDER,
                borderRight: ARCHIVED_DOCUMENTS_BORDER,
                pointerEvents: 'none',
              },
            }}
            colSpan={totalColumns}
          >
            <Collapse in={isArchivedSectionOpen} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  m: 1,
                  p: 1,
                  overflow: 'hidden',
                }}
              >
                <Typography variant="paragraph2" gutterBottom component="div">
                  Archived Documents ({archivedDocuments.length})
                </Typography>
                <Table aria-label="archived documents">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: STATUS_COLUMN_WIDTH,
                          backgroundColor: COLORS.primary.white,
                        }}
                      />
                      <TableCell
                        sx={{
                          width: REFERENCE_NUMBER_COLUMN_WIDTH,
                          maxWidth: REFERENCE_NUMBER_COLUMN_WIDTH,
                          backgroundColor: COLORS.primary.white,
                        }}
                      />
                      <TableCell sx={{ width: DOCUMENT_NAME_COLUMN_WIDTH }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ width: ACTIONS_COLUMN_WIDTH }}>
                        Actions
                      </TableCell>
                      <TableCell sx={{ minWidth: 65 }}>Issuer</TableCell>
                      <TableCell sx={{ minWidth: 65 }}>
                        Certificate Number
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Issue Date</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Expiry Date</TableCell>
                      <TableCell sx={{ minWidth: 65 }}>Last Survey</TableCell>
                      <TableCell sx={{ minWidth: 65 }}>Next Survey</TableCell>
                      <TableCell sx={{ minWidth: 65 }}>Remarks</TableCell>
                      <TableCell>Data Source</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        Version History
                      </TableCell>
                      <TableCell
                        sx={{
                          width: ARCHIVED_EXPAND_COLUMN_WIDTH,
                          backgroundColor: COLORS.primary.white,
                        }}
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {archivedDocuments
                      .sort(
                        (a, b) =>
                          new Date(b.createdOn).getTime() -
                          new Date(a.createdOn).getTime(),
                      )
                      .map((archived, index) => (
                        <TableRow key={`${archived.fileName}-${index}`}>
                          <TableCell
                            sx={{
                              width: STATUS_COLUMN_WIDTH,
                              backgroundColor: COLORS.primary.white,
                            }}
                          />
                          <TableCell
                            sx={{
                              width: REFERENCE_NUMBER_COLUMN_WIDTH,
                              maxWidth: REFERENCE_NUMBER_COLUMN_WIDTH,
                              backgroundColor: COLORS.primary.white,
                            }}
                          />
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ width: DOCUMENT_NAME_COLUMN_WIDTH }}
                          >
                            <DocumentsPreviewDownloadControls
                              vesselId={vesselId}
                              documentName={archived.certificateName}
                              tableRowItem={archived}
                              setStatusAnimatedAlert={setStatus}
                              versionId={archived.versionId}
                            />
                          </TableCell>
                          <TableCell sx={{ width: ACTIONS_COLUMN_WIDTH }} />
                          <TableCell>
                            <Typography variant="paragraph2">
                              {archived.metadata.certificateIssuer
                                ? certificateIssuerOptions.find(
                                    (option) =>
                                      option.value ===
                                      archived.metadata.certificateIssuer,
                                  )?.label
                                : FALLBACK_STRING_DASH}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="paragraph2">
                              {archived.metadata.certificateNumber
                                ? archived.metadata.certificateNumber
                                : FALLBACK_STRING_DASH}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="paragraph2">
                              {archived.metadata.issued
                                ? toDateFormat(
                                    archived.metadata.issued,
                                    dateFormat,
                                  )
                                : FALLBACK_STRING_DASH}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="paragraph2">
                              {archived.metadata.expires
                                ? toDateFormat(
                                    archived.metadata.expires,
                                    dateFormat,
                                  )
                                : FALLBACK_STRING_DASH}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="paragraph2">
                              {FALLBACK_STRING_DASH}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="paragraph2">
                              {FALLBACK_STRING_DASH}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="paragraph2">
                              {archived.metadata.remarks
                                ? archived.metadata.remarks
                                : FALLBACK_STRING_DASH}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="paragraph2">
                              {archived.metadata.uploadedByName
                                ? archived.metadata.uploadedByName
                                : FALLBACK_STRING_DASH}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="paragraph2">
                              {toDateFormat(
                                archived.createdOn,
                                dateFormatWithHoursAndMinutes,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: ARCHIVED_EXPAND_COLUMN_WIDTH,
                              backgroundColor: COLORS.primary.white,
                            }}
                          />
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export function DocumentsTableMui({
  certificateStatus,
  documents,
  isCommercialView,
  onCommercialViewChange,
  refetchDocuments,
  showTechManagerDocuments,
}: DocumentsTableMuiProps) {
  const { vesselId } = useParams()
  const [tagFilters, setTagFilters] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [sortField, setSortField] = useState<SortField>('documentName')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const state = useVesselPageState()
  const allowAddDocument = useCanEditAndAddDocument()
  const vesselPageState = useVesselPageState()
  const isInternallyTechManaged = state.vessel?.technicallyManaged

  const existingDocumentTags = [
    ...new Set(documents.flatMap((doc) => doc.tags).filter(Boolean)),
  ] as string[]

  const [documentNameFilter, setDocumentNameFilter] = useState('')
  const [debouncedDocumentNameFilter, setDebouncedDocumentNameFilter] =
    useState('')
  const [exportMenuOpen, setExportMenuOpen] = useState(false)
  const exportMenuRef = useRef<HTMLButtonElement>(null)
  const dateFormat = useDateFormat()

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDocumentNameFilter(documentNameFilter)
    }, 500)
    return () => {
      clearTimeout(handler)
    }
  }, [documentNameFilter])

  const {
    data: archivedDocumentsData,
    refetch: refetchArchivedDocuments,
    error: archivedError,
    isLoading: isArchivedLoading,
  } = useGetVesselArchivedDocuments(vesselId)
  const [showingArchived, setShowingArchived] = useState(false)

  const filteredDocuments = documents
    .filter((doc) => doc.documentName || doc.certificateCategories)
    .filter(
      (doc) =>
        doc.documentName
          .replaceAll(' ', '')
          .toLowerCase()
          .includes(
            debouncedDocumentNameFilter.replaceAll(' ', '').toLowerCase(),
          ) ||
        doc?.certificateCategories?.some((category) =>
          category
            .replaceAll(' ', '')
            .toLowerCase()
            .includes(
              debouncedDocumentNameFilter.replaceAll(' ', '').toLowerCase(),
            ),
        ),
    )
    .filter(
      (doc) =>
        tagFilters.length === 0 ||
        doc?.tags?.some((tag) => tagFilters.includes(tag)),
    )

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleExportCsv = () => {
    setExportMenuOpen(false)
    const headers = showTechManagerDocuments
      ? [
          'Type',
          'Reference Number',
          'Document Name',
          'Issuer',
          'Certificate Number',
          'Issue Date',
          'Expiry Date',
          'Last Survey',
          'Next Survey',
          'Remarks',
          'Data Source',
          'Version History',
        ]
      : [
          'Type',
          'Document Name',
          'Issue Date',
          'Expiry Date',
          'Data Source',
          'Version History',
        ]

    const rows = filteredDocuments.map((doc) => {
      if (showTechManagerDocuments) {
        return [
          doc.type,
          doc.referenceNumber ?? '',
          doc.documentName,
          doc.certificateIssuer
            ? (certificateIssuerOptions.find(
                (option) => option.value === doc.certificateIssuer,
              )?.label ?? '')
            : '',
          doc.certificateNumber ?? '',
          doc.issued ? toDateFormat(parseDateOnly(doc.issued), dateFormat) : '',
          doc.expires
            ? toDateFormat(parseDateOnly(doc.expires), dateFormat)
            : '',
          doc.lastSurvey
            ? toDateFormat(parseDateOnly(doc.lastSurvey), dateFormat)
            : '',
          doc.nextSurvey
            ? toDateFormat(parseDateOnly(doc.nextSurvey), dateFormat)
            : '',
          doc.remarks ?? '',
          doc.source ?? '',
          doc.updatedAt ? toDateFormat(doc.updatedAt, dateFormat) : '',
        ]
      }
      return [
        doc.type,
        doc.documentName,
        doc.issued ? toDateFormat(parseDateOnly(doc.issued), dateFormat) : '',
        doc.expires ? toDateFormat(parseDateOnly(doc.expires), dateFormat) : '',
        doc.source ?? '',
        doc.updatedAt ? toDateFormat(doc.updatedAt, dateFormat) : '',
      ]
    })

    const csvContent = [
      'sep=,',
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((cell) => {
            const value = String(cell)
            const safeValue = /^[\s\p{Cc}]*[=+\-@＝＋－＠]/u.test(value)
              ? `\t${value}`
              : value
            return `"${safeValue.replace(/"/g, '""')}"`
          })
          .join(','),
      ),
    ].join('\n')

    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csvContent], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `documents-${vesselPageState.vessel?.vesselName ?? vesselId}-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const handlePrint = () => {
    setExportMenuOpen(false)
    window.print()
  }

  const handleExportPdf = () => {
    setExportMenuOpen(false)
    window.print()
  }

  const groupedDocuments: DocumentTypeGroup[] = Object.entries(
    Object.groupBy(filteredDocuments, (doc) => doc.type),
  )
    .filter((entry): entry is [string, IDocumentsRowData[]] => entry[1] != null)
    .map(([type, docs]) => ({ type, documents: docs }))
    .sort((a, b) => {
      const order = ['Primary Certificates', 'Primary Certificates (Other)']
      const aIndex = order.indexOf(a.type)
      const bIndex = order.indexOf(b.type)
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
      if (aIndex !== -1) return -1
      if (bIndex !== -1) return 1
      return a.type.localeCompare(b.type)
    })

  const documentsToAddData = Object.groupBy(
    documents.filter((doc) => doc.readOnly !== true),
    (data) => data.type,
  )

  return (
    <Box>
      <Box
        className="print-hide"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {showTechManagerDocuments && (
            <Box
              className="tour-step-documents-filters"
              sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}
            >
              <TextField
                label="Search document name or categories..."
                variant="outlined"
                size="small"
                sx={{ width: '300px', mr: 2 }}
                value={documentNameFilter}
                onChange={(e) => setDocumentNameFilter(e.target.value)}
              />
              <Autocomplete
                limitTags={1}
                size="small"
                multiple
                disableCloseOnSelect
                options={existingDocumentTags.filter(
                  (tag) => !tagFilters.includes(tag),
                )}
                value={tagFilters}
                onChange={(_, newValue) => {
                  setTagFilters(newValue)
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Filter by tag..." />
                )}
                sx={{ minWidth: 344 }}
                slotProps={{ popper: { style: { zIndex: Z_INDEX.Popup } } }}
              />
            </Box>
          )}
          <Button
            className="tour-step-documents-bulk-select"
            startIcon={
              state.isBulkSelectActive ? <CloseIcon /> : <ImportIcon />
            }
            variant="text"
            sx={{ height: '36px', display: 'flex', alignItems: 'center' }}
            onClick={() =>
              state.setIsBulkSelectActive(!state.isBulkSelectActive)
            }
          >
            {state.isBulkSelectActive
              ? 'Cancel'
              : 'Select for download or email'}
          </Button>
          <Button
            className="tour-step-documents-export"
            ref={exportMenuRef}
            variant="text"
            startIcon={<ExportIcon />}
            onClick={() => setExportMenuOpen(true)}
            aria-controls={exportMenuOpen ? 'export-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={exportMenuOpen ? 'true' : undefined}
          >
            Export
          </Button>
          <Menu
            id="export-menu"
            anchorEl={exportMenuRef.current}
            open={exportMenuOpen}
            onClose={() => setExportMenuOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={handleExportPdf}>
              <ListItemIcon>
                <FileIcon size={18} />
              </ListItemIcon>
              <ListItemText>Export as PDF</ListItemText>
            </MenuItem>
            <MenuItem onClick={handlePrint}>
              <ListItemIcon>
                <FileIcon size={18} />
              </ListItemIcon>
              <ListItemText>Print</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportCsv}>
              <ListItemIcon>
                <FileIcon size={18} />
              </ListItemIcon>
              <ListItemText>Excel (CSV)</ListItemText>
            </MenuItem>
          </Menu>
          {isInternallyTechManaged && (
            <FormControlLabel
              className="tour-step-documents-commercial-view"
              control={
                <Switch
                  checked={isCommercialView}
                  onChange={(event) => {
                    setShowingArchived(false)
                    setDocumentNameFilter('')
                    setDebouncedDocumentNameFilter('')
                    setTagFilters([])
                    onCommercialViewChange(event.target.checked)
                  }}
                />
              }
              label="Commercial View"
            />
          )}
          {showTechManagerDocuments && (
            <FormControlLabel
              className="tour-step-documents-archived"
              control={
                <Switch
                  checked={showingArchived}
                  onChange={() => {
                    if (!archivedDocumentsData) {
                      refetchArchivedDocuments()
                    }
                    setShowingArchived(!showingArchived)
                  }}
                />
              }
              label="Show Archived Documents"
            />
          )}
        </Box>
        <Stack spacing={2} sx={{ alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {allowAddDocument && (
              <Button
                className="tour-step-documents-upload"
                variant="contained"
                startIcon={<AddIcon fontSize={20} />}
                onClick={() =>
                  showTechManagerDocuments
                    ? setIsBulkModalOpen(true)
                    : setIsModalOpen(true)
                }
              >
                Upload
              </Button>
            )}
          </Box>
          {certificateStatus}
        </Stack>
      </Box>

      <ScrollHintTableContainer
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
          '& table': { minWidth: 1200 },
          '@media print': { maxHeight: 'none' },
        }}
        className="print-expand tour-step-documents-table"
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: STATUS_COLUMN_WIDTH }} />
              {showTechManagerDocuments && (
                <TableCell
                  sx={{
                    width: REFERENCE_NUMBER_COLUMN_WIDTH,
                    maxWidth: REFERENCE_NUMBER_COLUMN_WIDTH,
                  }}
                />
              )}
              <TableCell
                className="tour-step-documents-sort"
                sx={{
                  width: DOCUMENT_NAME_COLUMN_WIDTH,
                  cursor: 'pointer',
                }}
                onClick={() => handleSort('documentName')}
              >
                <TableSortLabel
                  active={sortField === 'documentName'}
                  direction={
                    sortField === 'documentName' ? sortDirection : 'asc'
                  }
                  IconComponent={() =>
                    sortField === 'documentName' ? (
                      sortDirection === 'asc' ? (
                        <ChevronDownIcon
                          size={16}
                          color={COLORS.secondary.darkBlue}
                        />
                      ) : (
                        <ChevronUpIcon
                          size={16}
                          color={COLORS.secondary.darkBlue}
                        />
                      )
                    ) : null
                  }
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                className="tour-step-documents-actions-column"
                sx={{ width: ACTIONS_COLUMN_WIDTH }}
              >
                Actions
              </TableCell>
              {showTechManagerDocuments && (
                <TableCell
                  className="tour-step-documents-details-columns"
                  sx={{ minWidth: 65 }}
                >
                  Issuer
                </TableCell>
              )}
              {showTechManagerDocuments && (
                <TableCell sx={{ minWidth: 65 }}>Certificate Number</TableCell>
              )}
              <TableCell sx={{ minWidth: 100 }}>Issue Date</TableCell>
              <TableCell
                sx={{ minWidth: 100, cursor: 'pointer' }}
                onClick={() => handleSort('expires')}
              >
                <TableSortLabel
                  active={sortField === 'expires'}
                  direction={sortField === 'expires' ? sortDirection : 'asc'}
                  IconComponent={() =>
                    sortField === 'expires' ? (
                      sortDirection === 'asc' ? (
                        <ChevronDownIcon
                          size={16}
                          color={COLORS.secondary.darkBlue}
                        />
                      ) : (
                        <ChevronUpIcon
                          size={16}
                          color={COLORS.secondary.darkBlue}
                        />
                      )
                    ) : null
                  }
                >
                  Expiry Date
                </TableSortLabel>
              </TableCell>
              {showTechManagerDocuments && (
                <TableCell sx={{ minWidth: 65 }}>Last Survey</TableCell>
              )}
              {showTechManagerDocuments && (
                <TableCell sx={{ minWidth: 65 }}>Next Survey</TableCell>
              )}
              {showTechManagerDocuments && (
                <TableCell
                  className="tour-step-documents-remarks-column"
                  sx={{ minWidth: 65 }}
                >
                  Remarks
                </TableCell>
              )}
              <TableCell className="tour-step-documents-source-column">
                Data Source
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>Version History</TableCell>
              {showingArchived && !isArchivedLoading && (
                <TableCell sx={{ width: ARCHIVED_EXPAND_COLUMN_WIDTH }} />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedDocuments.map((group) => (
              <DocumentTypeRow
                key={group.type}
                group={group}
                vesselId={vesselId!}
                sortField={sortField}
                sortDirection={sortDirection}
                refetchDocuments={refetchDocuments}
                archivedDocuments={
                  showingArchived ? archivedDocumentsData : undefined
                }
                showingArchived={showingArchived}
                existingDocumentTags={existingDocumentTags}
                showTechManagerDocuments={showTechManagerDocuments}
              />
            ))}
          </TableBody>
        </Table>
      </ScrollHintTableContainer>

      <TMDocumentModal
        isModalOpen={isBulkModalOpen}
        setIsModalOpen={setIsBulkModalOpen}
        refetchDocuments={refetchDocuments}
        primaryDocumentNamesByType={documents.filter((document) =>
          isPrimaryCertificateType(document.type),
        )}
        vesselId={vesselId || ''}
        vesselName={vesselPageState.vessel?.vesselName || ''}
        existingDocumentTags={existingDocumentTags}
      />

      <DialogBox
        sx={{ width: '780px', maxWidth: 'none' }}
        open={isModalOpen}
        onClose={(e, reason) => {
          if (reason === 'backdropClick') return
          setIsModalOpen(false)
        }}
      >
        <Typography variant="h2" sx={{ mb: 4 }}>
          {`Add certificate ${vesselPageState.vessel?.vesselName ? `for ${vesselPageState.vessel?.vesselName}` : ''} `}
        </Typography>
        <AddDocumentModal
          existingDocumentTags={existingDocumentTags}
          showTechManagerDocuments={showTechManagerDocuments}
          refetchDocuments={refetchDocuments}
          documentNamesByType={Object.keys(documentsToAddData)
            .map((type) => ({
              type,
              documents: documentsToAddData[type] ?? [],
            }))
            .filter((group) => group.documents.length > 0)}
          close={() => setIsModalOpen(false)}
        />
      </DialogBox>
    </Box>
  )
}
